<template>
  <div class="payment-methods-wrapper">
    <div class="payment-info-title-text">
      {{ $t('plans.paymentsMethods') }}
    </div>

    <v-btn-toggle
      v-model="paymentInfo.paymentMethod"
      dense
      mandatory
      class="payment-methods-toggle-wrapper"
    >
      <v-btn
        v-for="paymentMethod in paymentsMethods"
        :key="`user-panel-plans-payment-method-${paymentMethod}`"
        class="payment-methods-item"
        :value="paymentMethod"
        color="primary"
        outlined
      >
        {{ getPaymentMethodName(paymentMethod) }}
      </v-btn>
    </v-btn-toggle>

    <div
      v-if="paymentsMethods.length > 0"
      class="payment-methods-section"
    >
      <v-switch
        v-if="$i18n.locale !== 'ua' && (paymentInfo.paymentMethod === PAYMENT_METHOD_CARD_NAME || paymentInfo.paymentMethod === PAYMENT_METHOD_UNLIMINT_NAME)"
        v-model="isLocalCard"
        inset
        flat
        hide-details
        class="payment-card-switcher"
        :label="$t('plans.cardCountry')"
        @change="changeCardCountry"
      />

      <crypto-payment-methods-list
        v-if="paymentInfo.paymentMethod === PAYMENT_METHOD_CRYPTO_NAME"
      />

      <usdt-payment-methods-list
        v-if="paymentInfo.paymentMethod === PAYMENT_METHOD_USDT_NAME"
        :estimate="estimate ? estimate : 0"
      />

      <wallet-payment-methods-list
        v-if="paymentInfo.paymentMethod === PAYMENT_METHOD_WALLET_NAME"
        :estimate="estimate ? estimate : 0"
      />

      <div
        v-if="paymentInfo.paymentMethod === PAYMENT_METHOD_CAPITALIST_NAME || paymentInfo.paymentMethod === PAYMENT_METHOD_WALLET_NAME"
      >
        <form
          ref="capitalist"
            name="payment"
            method="post"
            target="_blank"
            action="https://capitalist.net/merchant/pay?lang=en"
            accept-charset="UTF-8"
        >
          <input type="hidden" name="merchantid" :value="merchantCapitalistId" />
          <input type="hidden" name="number" :value="invoice.id" />
          <input type="hidden" name="amount" :value="invoice.sum" />
          <input type="hidden" name="currency" :value="invoice.currency" />
          <input type="hidden" name="description" :value="invoice.description" />
          <input type="hidden" name="opt_client_id" :value="invoice.teamId" />
          <input type="hidden" name="sign" :value="invoice.signature" />
        </form>
      </div>

      <form
        ref="paymentTarget"
        name="paymentTarget"
        method="post"
        target="_blank"
        action="#"
        accept-charset="UTF-8"
      />
    </div>
  </div>
</template>

<script>
  import { mapGetters } from "vuex";
  import { mapFields } from "vuex-map-fields";
  import {
    PAYMENT_METHOD_CARD_NAME,
    PAYMENT_METHOD_UNLIMINT_NAME,
    PAYMENT_METHOD_CRYPTO_NAME,
    PAYMENT_METHOD_USDT_NAME,
    PAYMENT_METHOD_WALLET_NAME,
    PAYMENT_METHOD_CAPITALIST_NAME,
    PAYMENT_METHOD_STRIPE,
    USDT_TRC20_CODE
  } from "@/constants/plans";
  import CryptoPaymentMethodsList from "@/components/plans/dialogs/components/paymentMethods/crypto/CryptoPaymentMethodsList";
  import UsdtPaymentMethodsList from "@/components/plans/dialogs/components/paymentMethods/usdt/UsdtPaymentMethodsList";
  import WalletPaymentMethodsList from "@/components/plans/dialogs/components/paymentMethods/wallet/WalletPaymentMethodsList";
  import featureFlags from '@/mixins/featureFlags'
  import { cloneDeep } from 'lodash'
  import { countriesListEn } from '@/constants/countries'

  export default {
    name: 'PaymentDialogPaymentMethods',

    components: {
      CryptoPaymentMethodsList,
      UsdtPaymentMethodsList,
      WalletPaymentMethodsList
    },

    mixins: [featureFlags],

    props: {
      estimate: {
        type: Number,
        required: true,
        default: 0
      }
    },

    data () {
      return {
        PAYMENT_METHOD_STRIPE,
        PAYMENT_METHOD_CARD_NAME,
        PAYMENT_METHOD_UNLIMINT_NAME,
        PAYMENT_METHOD_CRYPTO_NAME,
        PAYMENT_METHOD_USDT_NAME,
        PAYMENT_METHOD_WALLET_NAME,
        PAYMENT_METHOD_CAPITALIST_NAME,
        USDT_TRC20_CODE,
        merchantCapitalistId: process.env.VUE_APP_CAPITALIST_SHOP_ID || 102504,
        isDev:  process.env.NODE_ENV !== 'production',
        shopId: process.env.VUE_APP_CRYPTADIUM_SHOP_ID || 'aduRgr1fLt'
      }
    },

    computed: {
      ...mapGetters({
        paymentInfo: 'plans/paymentInfo',
        paymentsMethodsList: 'plans/paymentMethods',
        countryCode: 'plans/countryCode'
      }),
      ...mapFields('plans', ['invoice', 'paymentMethods']),

      paymentsMethods () {
        const isStripeEnabled = this.isFeatureEnabled('stripe-feature-flag')
        const clonedPaymentsMethods = cloneDeep(this.paymentMethods)

        const userProvider = countriesListEn.find(
          (el) => this.countryCode && el.value === this.countryCode
        )?.provider

        if (isStripeEnabled && userProvider) {
          return clonedPaymentsMethods.filter(el => el !==
            (userProvider === PAYMENT_METHOD_STRIPE
              ? PAYMENT_METHOD_UNLIMINT_NAME
              : PAYMENT_METHOD_STRIPE)
          )
        }

        return this.paymentMethods.filter(el => {
          return el !== PAYMENT_METHOD_STRIPE || isStripeEnabled
        })
      },
      isLocalCard: {
        get () {
          return this.$store.state.plans.isLocalCard
        },
        set (value) {
          this.$store.dispatch('plans/setIsLocalCard', value)
        }
      }
    },

    watch: {
      'paymentInfo.paymentMethod': {
        handler (value) {
          // Мы не показываем переключатель "карта Российского банка" для Украины
          if (value === PAYMENT_METHOD_CARD_NAME && this.$i18n.locale === 'ua') {
            this.isLocalCard = false
            this.changeCardCountry()
          }
        }
      }
    },

    async created () {
      await this.$store.dispatch('plans/paymentMethod', PAYMENT_METHOD_USDT_NAME)
    },

    methods: {
      getPaymentMethodName(paymentMethod) {
        return this.$t(`plans.${paymentMethod === PAYMENT_METHOD_STRIPE ? 'paymentMethodStripe' : paymentMethod}`)
      },
      async changeCardCountry () {
        if (this.isLocalCard) {
          this.paymentsMethods.splice(0, 1, PAYMENT_METHOD_CARD_NAME)
          await this.$store.dispatch('plans/paymentMethod', PAYMENT_METHOD_CARD_NAME)
        } else {
          this.paymentsMethods.splice(0, 1, PAYMENT_METHOD_UNLIMINT_NAME)
          await this.$store.dispatch('plans/paymentMethod', PAYMENT_METHOD_UNLIMINT_NAME)
        }
      }
    }
  }
</script>

<style>
  .payment-methods-wrapper {
    margin-top: 24px;
  }

  .payment-methods-item {
    flex: 1;
    height: 28px!important;
    background: rgba(181, 71, 233, 0.1)!important;
    border-radius: 4px!important;
    border: unset!important;
  }

  .payment-methods-item .v-btn__content {
    font-weight: 600!important;
    font-size: 12px!important;
    line-height: 14px!important;
    letter-spacing: 1.25px!important;
    text-transform: uppercase;
  }

  .payment-methods-toggle-wrapper {
    display: flex;
    margin-top: 8px;
    gap: 8px;
    width: 100%;
  }

  .payment-methods-wrapper .v-btn-toggle > .v-btn.v-btn--active {
    background: #B547E9!important;
    color: #FFFFFF!important;
  }

  .payment-card-switcher {
    margin-top: 24px!important;
    margin-bottom: 0!important;
  }

  .payment-card-switcher .v-input--switch__thumb {
    left: 5px!important;
  }

  .payment-card-switcher .v-input--switch__track {
    left: 0!important;
  }

  .payment-card-switcher .v-input--selection-controls__ripple {
    left: -10px!important;
  }
</style>
